import React, { useEffect, useState } from "react";
import {
    Tabs,
    Button,
    Drawer,
    Input,
    Form,
    Checkbox,
    Alert,
    Radio,
    Space,
    Card,
    Upload,
    Modal,
    DatePicker,
    TimePicker,
    Typography,
    message,
    Grid,
} from "antd";
import {
    ArrowRightOutlined,
    AudioOutlined,
    RobotOutlined,
    EditOutlined,
    CalendarOutlined,
    LinkOutlined,
    GlobalOutlined,
    ClockCircleOutlined,
    FileImageOutlined,
    UploadOutlined,
    CloudUploadOutlined,
    DeleteOutlined,
    FilePdfOutlined,
    FileWordOutlined,
    FileExcelOutlined,
    FileTextOutlined,
    FileMarkdownOutlined,
    FileZipOutlined,
    FileOutlined,
    FireOutlined,
} from "@ant-design/icons";
import AddEventModal from "./AddEventModal";
import { CanvasWrapper } from "./IdeaCanvasModal";
import styles from "./styles/NewAddPanel.module.css";
import SourcesInputBox from "../ai_home/SourcesInputBox";
import ExternalPostPanel from "./add_post/ExternalPostPanel";
import dayjs from "dayjs";
import { useBotEndpoint } from "../_actions/bot";
import { useUploadFile } from "../_actions/files";
import { GeneratedIdeasBox, GeneratedIdeasCore } from "./CreationFromLinkBox";
import log from "cslog";
import { queryClient } from "../_helpers/query";
import SocialAccountSelection from "../social_media/SocialAccountSelection";
import TrendsNew from "../trends/TrendsNew";
import MobileNewAddPanel from "./MobileNewAddPanel";

const { TextArea } = Input;

export const panelConfig = [
    {
        key: "external",
        label: "External",
        description: "Post external content",
        icon: GlobalOutlined,
        color: "#1677ff",
        bgColor: "#e6f4ff",
    },
    {
        key: "auto",
        label: "Auto",
        description: "AI-generated ideas",
        icon: RobotOutlined,
        color: "#722ed1",
        bgColor: "#f9f0ff",
    },
    {
        key: "write",
        label: "Write",
        description: "Write your idea",
        icon: EditOutlined,
        color: "#52c41a",
        bgColor: "#f6ffed",
    },
    {
        key: "link",
        label: "Link",
        description: "Generate from URL",
        icon: LinkOutlined,
        color: "#fa8c16",
        bgColor: "#fff7e6",
    },
    {
        key: "image",
        label: "Image",
        description: "Coming soon",
        icon: FileImageOutlined,
        color: "#bfbfbf",
        bgColor: "#f5f5f5",
        disabled: true,
    },
    {
        key: "document",
        label: "Document",
        description: "Upload & analyze",
        icon: FileOutlined,
        color: "#13c2c2",
        bgColor: "#e6fffb",
    },
    {
        key: "add-event",
        label: "Event",
        description: "Create for events",
        icon: CalendarOutlined,
        color: "#f5222d",
        bgColor: "#fff1f0",
    },
    {
        key: "trend",
        label: "Trend",
        description: "Ride trending topics",
        icon: FireOutlined,
        color: "#fa541c",
        bgColor: "#fff2e8",
    },
];

function NewCreationPanel({ me, action, close }) {
    return (
        <Tabs
            type="card"
            items={[
                {
                    key: "external",
                    label: "External",
                    icon: <GlobalOutlined />,
                    children: (
                        <ExternalPostPanel
                            me={me}
                            action={action}
                            onClose={close}
                        />
                    ),
                },
                {
                    key: "auto",
                    label: "Auto",
                    icon: <RobotOutlined />,
                    children: <AutoPanel me={me} />,
                },
                {
                    key: "wite",
                    label: "Write",
                    icon: <EditOutlined />,
                    children: <WritePanel me={me} />,
                },
                {
                    key: "link",
                    label: "Link",
                    icon: <LinkOutlined />,
                    children: <LinkPanel me={me} />,
                },
                {
                    key: "image",
                    label: "Image",
                    icon: <UploadOutlined />,
                    children: <p>Coming soon...</p>,
                },
                {
                    key: "document",
                    label: "Document",
                    icon: <FileOutlined />,
                    children: <DocumentPanel me={me} />,
                },
                {
                    key: "add-event",
                    label: "Event",
                    icon: <CalendarOutlined />,
                    children: (
                        <div style={{ maxWidth: "600px" }}>
                            <h3>Generate for Event</h3>
                            <AddEventModal
                                org={me.selected_org?.id}
                                onAdded={close}
                                initialDate={action.date}
                            />
                        </div>
                    ),
                },
                {
                    key: "trend",
                    label: "Trend",
                    icon: <FireOutlined />,
                    children: <TrendPanel me={me} />,
                },
            ]}
        />
    );
}

function NewAddPanel({ action, setCellActionModal, me = {} }) {
    const { useBreakpoint } = Grid;
    const screens = useBreakpoint();
    const isMobile = !screens.sm;

    const close = () => {
        setCellActionModal({ open: false, date: null });
        queryClient.invalidateQueries(["items", "qdb_calendar_entries"]);
    };

    const renderPanelContent = (key) => {
        switch (key) {
            case "external":
                return (
                    <ExternalPostPanel
                        me={me}
                        action={action}
                        onClose={close}
                    />
                );
            case "auto":
                return <AutoPanel me={me} />;
            case "write":
                return <WritePanel me={me} />;
            case "link":
                return <LinkPanel me={me} />;
            case "image":
                return (
                    <p style={{ color: "#999", padding: "20px 0" }}>
                        Coming soon...
                    </p>
                );
            case "document":
                return <DocumentPanel me={me} />;
            case "add-event":
                return (
                    <div style={{ maxWidth: "600px" }}>
                        <h3>Generate for Event</h3>
                        <AddEventModal
                            org={me.selected_org?.id}
                            onAdded={close}
                            initialDate={action.date}
                        />
                    </div>
                );
            case "trend":
                return <TrendPanel me={me} />;
            default:
                return null;
        }
    };

    if (isMobile) {
        return (
            <MobileNewAddPanel
                open={action.open}
                onClose={close}
                panelConfig={panelConfig}
                renderContent={renderPanelContent}
            />
        );
    }

    return (
        <CanvasWrapper
            open={action.open}
            setOpen={(v) => {
                if (!v) close();
            }}
            title="Add to calendar"
            actions={[
                <Button key="close" type="text" onClick={close}>
                    Close
                </Button>,
            ]}
            width="60vw"
        >
            <div className={styles.wrapper}>
                <div className={styles.left_col}>
                    <NewCreationPanel me={me} action={action} close={close} />
                </div>
            </div>
        </CanvasWrapper>
    );
}

export default NewAddPanel;

export function MobileCreatePanelStandalone({ open, onClose, me = {} }) {
    const action = { open, date: null };
    const renderContent = (key) => {
        switch (key) {
            case "external":
                return (
                    <ExternalPostPanel
                        me={me}
                        action={action}
                        onClose={onClose}
                    />
                );
            case "auto":
                return <AutoPanel me={me} />;
            case "write":
                return <WritePanel me={me} />;
            case "link":
                return <LinkPanel me={me} />;
            case "image":
                return (
                    <p style={{ color: "#999", padding: "20px 0" }}>
                        Coming soon...
                    </p>
                );
            case "document":
                return <DocumentPanel me={me} />;
            case "add-event":
                return (
                    <div style={{ maxWidth: "600px" }}>
                        <h3>Generate for Event</h3>
                        <AddEventModal
                            org={me.selected_org?.id}
                            onAdded={onClose}
                        />
                    </div>
                );
            case "trend":
                return <TrendPanel me={me} />;
            default:
                return null;
        }
    };
    return (
        <MobileNewAddPanel
            open={open}
            onClose={onClose}
            panelConfig={panelConfig}
            renderContent={renderContent}
        />
    );
}

function RandomIdeasPanel({ count = 6 }) {
    const [ideas, setIdeas] = React.useState(() => generateRandomIdeas(count));
    const refresh = () => setIdeas(generateRandomIdeas(count));

    return (
        <div
            style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 8,
                }}
            >
                <h4 style={{ margin: 0 }}>Random ideas</h4>
                <Button size="small" onClick={refresh}>
                    Refresh
                </Button>
            </div>
            <div
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 8,
                    overflow: "auto",
                    // Adjust this to fit nicely within the drawer
                    maxHeight: "calc(100vh - 220px)",
                }}
            >
                {ideas.map((txt, idx) => (
                    <div
                        key={idx}
                        style={{
                            border: "1px solid #f0f0f0",
                            borderRadius: 8,
                            padding: 10,
                            background: "#fafafa",
                            lineHeight: 1.4,
                        }}
                    >
                        {txt}
                    </div>
                ))}
            </div>
        </div>
    );
}

function generateRandomIdeas(n) {
    const topics = [
        "Promo",
        "Behind-the-scenes",
        "Customer spotlight",
        "How-to",
        "Tip of the day",
        "UGC",
        "Poll",
        "Trend",
        "FAQ",
        "Milestone",
    ];
    const hooks = [
        "Ask a question",
        "Share a quick tip",
        "Tell a story",
        "Use a bold stat",
        "Start with Why",
        "Myth vs fact",
        "Do/Don’t list",
    ];
    const ctas = [
        "Comment below",
        "Share this",
        "Save for later",
        "Tag a friend",
        "Visit the link in bio",
        "Try it today",
    ];
    const emojis = ["✨", "🔥", "💡", "📣", "🎯", "📊", "🛠️", "🧠", "🎉", "📅"];

    const out = [];
    for (let i = 0; i < n; i++) {
        const t = topics[Math.floor(Math.random() * topics.length)];
        const h = hooks[Math.floor(Math.random() * hooks.length)];
        const c = ctas[Math.floor(Math.random() * ctas.length)];
        const e = emojis[Math.floor(Math.random() * emojis.length)];
        out.push(`${e} ${t}: ${h}. ${c}.`);
    }
    return out;
}

export function TrendPanel({ me }) {
    return (
        <div style={{ marginTop: 10 }}>
            <TrendsNew me={me} isPanel={true} />
        </div>
    );
}

export function WritePanel({ me }) {
    const [finalText, setFinalText] = React.useState("");
    const [interimText, setInterimText] = React.useState("");
    const [isListening, setIsListening] = React.useState(false);
    const recognitionRef = React.useRef(null);

    const [include_ai, setIncludeAI] = useState(true);

    React.useEffect(() => {
        if (
            "webkitSpeechRecognition" in window ||
            "SpeechRecognition" in window
        ) {
            const SpeechRecognition =
                window.SpeechRecognition || window.webkitSpeechRecognition;
            recognitionRef.current = new SpeechRecognition();
            recognitionRef.current.continuous = true;
            recognitionRef.current.interimResults = true;
            recognitionRef.current.onresult = (event) => {
                let interimTranscript = "";
                let finalTranscript = "";
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const transcript = event.results[i][0].transcript;
                    if (event.results[i].isFinal) {
                        finalTranscript += transcript;
                    } else {
                        interimTranscript += transcript;
                    }
                }
                if (finalTranscript) {
                    setFinalText((prev) => prev + finalTranscript);
                }
                setInterimText(interimTranscript);
            };
            recognitionRef.current.onend = () => {
                setIsListening(false);
                setInterimText("");
            };
            recognitionRef.current.onerror = () => {
                setIsListening(false);
            };
        }
    }, []);

    const toggleListening = () => {
        if (isListening) {
            recognitionRef.current?.stop();
        } else {
            recognitionRef.current?.start();
            setIsListening(true);
        }
    };

    const combinedText = finalText + interimText;

    return (
        <AiGenerator
            me={me}
            title="Write your idea"
            renderSummaryContent={() => (
                <Typography.Paragraph
                    ellipsis={{ rows: 2, expandable: true, symbol: "more" }}
                    style={{ margin: 0, paddingRight: 8 }}
                >
                    {combinedText}
                </Typography.Paragraph>
            )}
            renderInput={({ mutate, loading, platforms }) => (
                <Form layout="vertical">
                    <Form.Item>
                        <div style={{ position: "relative" }}>
                            <TextArea
                                rows={12}
                                value={combinedText}
                                onChange={(e) => setFinalText(e.target.value)}
                            />
                            <Button
                                onClick={toggleListening}
                                style={{
                                    position: "absolute",
                                    bottom: 8,
                                    right: 8,
                                    background: "transparent",
                                    border: "none",
                                    padding: 0,
                                    zIndex: 1,
                                }}
                                icon={
                                    <AudioOutlined
                                        style={{
                                            color: isListening
                                                ? "#ff4d4f"
                                                : "#1890ff",
                                            fontSize: 18,
                                        }}
                                    />
                                }
                                className={isListening ? styles.pulsing : ""}
                                title={
                                    isListening ? "Stop Voice" : "Start Voice"
                                }
                            />
                        </div>
                    </Form.Item>

                    <Form.Item>
                        <Checkbox
                            onChange={(e) => setIncludeAI(e.target.checked)}
                            checked={include_ai}
                            style={{ userSelect: "none" }}
                        >
                            Let AI use it's knowledge to enhance your idea.
                        </Checkbox>
                    </Form.Item>
                    <Form.Item>
                        <Alert
                            type="info"
                            message={
                                include_ai
                                    ? "AI will use it's knowledge to enhance your idea."
                                    : "AI will use only this text to generate idea."
                            }
                        />
                    </Form.Item>
                    <Button
                        type="primary"
                        icon={<ArrowRightOutlined />}
                        iconPosition="end"
                        loading={loading}
                        disabled={
                            !combinedText.trim() || platforms?.length === 0
                        }
                        onClick={() =>
                            mutate({
                                text: combinedText,
                                include_ai: include_ai,
                                include_org: true,
                            })
                        }
                    >
                        Generate
                    </Button>
                </Form>
            )}
        />
    );
}

const themeLabels = {
    general: "General 📝",
    value_added: "Value Added 💎",
    engagement_driven: "Engagement Driven 👥",
    promotional: "Promotional 📢",
    random: "Random 🎲",
};

export function AutoPanel({ me }) {
    const [theme, setTheme] = useState(null);

    return (
        <AiGenerator
            me={me}
            title="Let AI Generate ideas for you"
            renderSummaryContent={() => (
                <span>
                    Selected Theme:{" "}
                    <strong>{themeLabels[theme] || theme}</strong>
                </span>
            )}
            renderInput={({ mutate, loading, platforms }) => (
                <Form layout="vertical">
                    <Form.Item label="Content Theme">
                        <ContentThemePicker value={theme} onChange={setTheme} />
                    </Form.Item>
                    <Form.Item style={{ marginTop: "20px" }}>
                        <Button
                            type="primary"
                            icon={<ArrowRightOutlined />}
                            iconPosition="end"
                            disabled={theme === null || platforms?.length === 0}
                            onClick={() =>
                                mutate({
                                    theme: theme,
                                    include_org: true,
                                })
                            }
                            loading={loading}
                        >
                            Generate
                        </Button>
                    </Form.Item>
                </Form>
            )}
        />
    );
}

export function LinkPanel({ me }) {
    const [link, setLink] = useState("");
    const [error, setError] = useState("");

    const handleChange = (e) => {
        const value = e.target.value.trim();
        if (value && !isValidUrl(value)) {
            setError("Please enter a valid URL");
        } else {
            setError("");
        }
        setLink(value);
    };

    const isValidUrl = (url) => {
        try {
            let trimmed = url.trim();
            if (trimmed.startsWith("www.")) {
                trimmed = "https://" + trimmed;
            }
            if (
                !trimmed.startsWith("http://") &&
                !trimmed.startsWith("https://")
            ) {
                trimmed = "https://" + trimmed;
            }
            const parsed = new URL(trimmed);
            if (!["http:", "https:"].includes(parsed.protocol)) {
                return false;
            }
            const domain = parsed.hostname;
            const domainParts = domain.split(".");
            if (domainParts.length < 2) return false;
            const tld = domainParts[domainParts.length - 1];
            if (tld.length < 2 || !/^[a-zA-Z]+$/.test(tld)) return false;
            for (const part of domainParts) {
                if (!part || !/^[a-zA-Z0-9-]+$/.test(part)) return false;
            }
            return true;
        } catch (err) {
            return false;
        }
    };

    return (
        <AiGenerator
            me={me}
            title="Generate from Link"
            endpoint="/trends/analyse_source"
            renderSummaryContent={() => (
                <Typography.Paragraph
                    ellipsis={{ rows: 1, expandable: true, symbol: "more" }}
                    style={{ margin: 0, paddingRight: 8 }}
                >
                    Link:{" "}
                    <a href={link} target="_blank" rel="noopener noreferrer">
                        {link}
                    </a>
                </Typography.Paragraph>
            )}
            renderInput={({ mutate, loading, platforms }) => (
                <Form layout="vertical">
                    <Form.Item
                        validateStatus={error ? "error" : ""}
                        help={error}
                    >
                        <Input
                            placeholder="Enter valid link (e.g., https://example.com)"
                            onChange={handleChange}
                            value={link}
                            status={error ? "error" : ""}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Button
                            type="primary"
                            icon={<ArrowRightOutlined />}
                            iconPosition="end"
                            loading={loading}
                            disabled={
                                !link || !!error || platforms?.length === 0
                            }
                            onClick={() =>
                                mutate({
                                    idea_type: "normal",
                                    link: link,
                                    include_org: true,
                                })
                            }
                        >
                            Generate
                        </Button>
                    </Form.Item>
                </Form>
            )}
        />
    );
}

const getFileIcon = (fileName) => {
    if (!fileName) return FileOutlined;
    const extension = fileName.split(".").pop().toLowerCase();
    switch (extension) {
        case "pdf":
            return FilePdfOutlined;
        case "doc":
        case "docx":
            return FileWordOutlined;
        case "txt":
            return FileTextOutlined;
        case "xls":
        case "xlsx":
        case "csv":
            return FileExcelOutlined;
        default:
            return FileOutlined;
    }
};

function UploadedFileView({ file, onDelete, docId }) {
    if (!file) return null;
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                // If deleting is allowed (input mode), we might want to ensure it looks contained.
                // But here we just render the content. The container styling is handled by parent/wrapper.
            }}
        >
            {React.createElement(getFileIcon(file.name), {
                style: { fontSize: "24px", color: "#1890ff", marginRight: 12 },
            })}
            <div style={{ flex: 1, overflow: "hidden" }}>
                <Typography.Text strong ellipsis>
                    {file.name}
                </Typography.Text>
                <div style={{ fontSize: 12, color: "#666" }}>
                    {(file.size / 1024 / 1024).toFixed(2)} MB{" "}
                    {docId !== undefined &&
                        (docId ? "• Uploaded" : "• Uploading...")}
                </div>
            </div>
            {onDelete && (
                <Button
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={onDelete}
                    danger
                />
            )}
        </div>
    );
}

export function DocumentPanel({ me }) {
    const [file, setFile] = useState(null);
    const [docId, setDocId] = useState(null);
    const [uploadError, setUploadError] = useState("");

    const mutationUpload = useUploadFile(
        (data) => {
            setDocId(data?.id);
            message.success("File uploaded successfully");
        },
        (error) => {
            message.error("Failed to upload: " + error.message);
            setUploadError("Failed to upload file");
            setFile(null);
        }
    );

    const allowedExtensions = ["pdf", "doc", "docx", "txt", "csv"];

    const validateFile = (uploadFile) => {
        const fileExtension = uploadFile.name.split(".").pop().toLowerCase();
        if (!allowedExtensions.includes(fileExtension)) {
            setUploadError(
                "Only PDF, DOC, DOCX, CSV and TXT files are supported"
            );
            return false;
        }

        const maxSize = 5 * 1024 * 1024; // 5MB
        if (uploadFile.size > maxSize) {
            setUploadError("File size must be less than 5MB");
            return false;
        }

        setUploadError("");
        return true;
    };

    const handleFileUpload = (uploadFile) => {
        if (validateFile(uploadFile)) {
            setFile(uploadFile);
            setDocId(null); // Reset docId when new file is selected
            const formData = new FormData();
            formData.append("file", uploadFile);
            mutationUpload.mutate(formData);
        }
        return false; // Prevent default upload behavior
    };

    const removeFile = () => {
        setFile(null);
        setDocId(null);
        setUploadError("");
    };

    return (
        <AiGenerator
            me={me}
            title="Generate from Document"
            endpoint="/trends/analyse_source"
            renderSummaryContent={() => <UploadedFileView file={file} />}
            renderInput={({ mutate, loading, platforms }) => (
                <Form layout="vertical">
                    <Form.Item
                        validateStatus={uploadError ? "error" : ""}
                        help={uploadError}
                    >
                        {!file ? (
                            <Upload.Dragger
                                beforeUpload={handleFileUpload}
                                showUploadList={false}
                                className={styles.modalDragger}
                                customRequest={() => {}} // dummy to prevent auto upload if allowed
                            >
                                <div style={{ padding: "20px 0" }}>
                                    <CloudUploadOutlined
                                        style={{
                                            fontSize: 32,
                                            color: "#1890ff",
                                        }}
                                    />
                                    <div style={{ marginTop: 8 }}>
                                        <p style={{ margin: 0 }}>
                                            Drag & drop files here or browse
                                        </p>
                                        <p
                                            style={{
                                                margin: 0,
                                                fontSize: 12,
                                                color: "#999",
                                            }}
                                        >
                                            PDF, DOC, DOCX, TXT up to 5MB
                                        </p>
                                    </div>
                                </div>
                            </Upload.Dragger>
                        ) : (
                            <div
                                style={{
                                    padding: "12px",
                                    border: "1px solid #d9d9d9",
                                    borderRadius: "8px",
                                    background: "#fafafa",
                                }}
                            >
                                <UploadedFileView
                                    file={file}
                                    onDelete={removeFile}
                                    docId={docId}
                                />
                            </div>
                        )}
                    </Form.Item>
                    <Form.Item>
                        <Button
                            type="primary"
                            icon={<ArrowRightOutlined />}
                            iconPosition="end"
                            loading={
                                loading || (file && !docId && !uploadError)
                            } // Loading if generating OR uploading
                            disabled={!docId || platforms?.length === 0}
                            onClick={() =>
                                mutate({
                                    idea_type: "normal",
                                    doc: docId,
                                    include_org: true,
                                })
                            }
                        >
                            Generate
                        </Button>
                    </Form.Item>
                </Form>
            )}
        />
    );
}

function AiGenerator({
    me,
    title,
    endpoint = "/newgen/generate_ideas",
    renderInput,
    renderSummaryContent,
}) {
    const [result, setResult] = useState([]);
    const [open, setOpen] = useState(false);
    const [platforms, setPlatforms] = useState([]);
    const [fullPlatforms, setFullPlatforms] = useState([]);
    const mutationBot = useBotEndpoint(
        endpoint,
        (result) => {
            log.d(result, "AiGenerator result");
            if (result?.success) {
                setResult(result?.data);
                setOpen(true);
            }
        },
        () => {}
    );

    const wrappedMutate = (data) => {
        const payload = {
            ...data,
            platforms: fullPlatforms.map((plf) => ({
                platform: plf.platform,
                id: plf.id,
            })),
        };
        log.d(payload, "payload for AiGenerator");
        mutationBot.mutate(payload);
    };

    return (
        <div>
            <h3>{title}</h3>
            <SocialAccountSelection
                org_id={me?.selected_org?.id}
                value={platforms}
                onChange={setPlatforms}
                onChangeFull={setFullPlatforms}
            />
            {!open ? (
                renderInput({
                    mutate: wrappedMutate,
                    loading: mutationBot.isLoading,
                    platforms,
                })
            ) : (
                <>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "20px",
                            padding: "12px",
                            border: "1px solid #f0f0f0",
                            borderRadius: "8px",
                            background: "#fafafa",
                        }}
                    >
                        {renderSummaryContent()}
                        <Button
                            type="text"
                            icon={<EditOutlined />}
                            onClick={() => setOpen(false)}
                        />
                    </div>

                    <GeneratedIdeasCore
                        result={result}
                        me={me}
                        generation_type="normal"
                        platforms={fullPlatforms}
                    />
                </>
            )}
        </div>
    );
}

export function ContentThemePicker({ value, onChange }) {
    return (
        <Radio.Group
            // optionType="button"
            // buttonStyle="solid"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            defaultValue={value}
        >
            <Space direction="vertical">
                {Object.entries(themeLabels).map(([key, label]) => (
                    <Radio key={key} value={key}>
                        {label}
                    </Radio>
                ))}
            </Space>
        </Radio.Group>
    );
}

// schedule date time picker
export function SchedulePicker({ initial_value, onSchedule, trigger }) {
    const [open, setOpen] = useState(false);
    const [dt, setDt] = useState(null);
    const { useBreakpoint } = Grid;
    const screens = useBreakpoint();
    const isMobile = !screens.sm;
    const nativeInputRef = React.useRef(null);
    const [nativeFailed, setNativeFailed] = useState(false);

    const handleTriggerClick = () => {
        setDt(initial_value ? dayjs(initial_value) : null);
        if (isMobile && !nativeFailed) {
            const input = nativeInputRef.current;
            if (input) {
                input.value = initial_value
                    ? dayjs(initial_value).format("YYYY-MM-DDTHH:mm")
                    : "";
                try {
                    input.showPicker();
                    return;
                } catch (e) {
                    setNativeFailed(true);
                }
            }
        }
        setOpen(true);
    };

    const handleNativeChange = (e) => {
        if (e.target.value) {
            onSchedule(dayjs(e.target.value));
        }
    };

    const handleSchedule = () => {
        if (dt) {
            onSchedule(dt);
            setOpen(false);
        } else {
            setOpen(false);
        }
    };

    const handleMobileDateChange = (val) => {
        if (!val) {
            setDt(null);
            return;
        }
        setDt((prev) =>
            prev
                ? val.hour(prev.hour()).minute(prev.minute()).second(0)
                : val.second(0)
        );
    };

    const handleMobileTimeChange = (val) => {
        if (!val) return;
        setDt((prev) => {
            const base = prev || dayjs();
            return base.hour(val.hour()).minute(val.minute()).second(0);
        });
    };

    // Hidden input style — keeps the AntD DatePicker input invisible while the
    // popup renders inline via popupStyle={{ position: "static" }}
    const hiddenInputStyle = {
        width: 1,
        height: 1,
        padding: 0,
        border: 0,
        margin: 0,
        opacity: 0,
        overflow: "hidden",
        position: "absolute",
        zIndex: -1,
    };

    return (
        <>
            <span onClick={handleTriggerClick}>{trigger}</span>
            {isMobile && (
                <input
                    ref={nativeInputRef}
                    type="datetime-local"
                    onChange={handleNativeChange}
                    style={{
                        position: "absolute",
                        opacity: 0,
                        pointerEvents: "none",
                        width: 0,
                        height: 0,
                    }}
                />
            )}
            <Modal
                open={open}
                onCancel={() => setOpen(false)}
                footer={[
                    <Button key="cancel" onClick={() => setOpen(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="schedule"
                        type="primary"
                        onClick={handleSchedule}
                    >
                        Schedule
                    </Button>,
                ]}
                centered
                width={isMobile ? "95vw" : "fit-content"}
                style={isMobile ? { maxWidth: "100vw" } : undefined}
                closable={false}
                title="Schedule Post"
                styles={{
                    container: isMobile ? { padding: "18px 8px 8px 8px" } : {},
                    header: isMobile
                        ? { textAlign: "center", fontSize: "1.1em" }
                        : {},
                    footer: isMobile ? { textAlign: "center" } : {},
                }}
            >
                {isMobile ? (
                    // Mobile: date-only calendar inline, time picker below
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 16,
                            width: "100%",

                            transform: isMobile ? "scale(0.9)" : "none",
                            // transformOrigin: "top left",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                // justifyContent: "center",
                                // overflowX: "hidden",
                            }}
                        >
                            <DatePicker
                                open
                                value={dt}
                                getPopupContainer={(triggerNode) =>
                                    triggerNode.parentNode
                                }
                                onCalendarChange={(val) => {
                                    if (val && !Array.isArray(val)) {
                                        handleMobileDateChange(val);
                                    } else if (Array.isArray(val) && val[0]) {
                                        handleMobileDateChange(val[0]);
                                    }
                                }}
                                onChange={handleMobileDateChange}
                                style={hiddenInputStyle}
                                popupStyle={{ position: "static" }}
                                popupClassName={styles.noShadowPicker}
                                showNow={false}
                                needConfirm={false}
                                inputReadOnly
                            />
                        </div>
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                padding: "0 4px",
                                width: "100%",
                            }}
                        >
                            {/* <ClockCircleOutlined
                                style={{ color: "#1677ff", fontSize: 16 }}
                            />
                            <span
                                style={{
                                    fontWeight: 500,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                Time:
                            </span> */}
                            <TimePicker
                                value={dt}
                                onChange={handleMobileTimeChange}
                                format="HH:mm"
                                minuteStep={5}
                                style={{ flex: 1 }}
                                placeholder="Select time"
                                size="large"
                                showNow
                            />
                        </div>
                    </div>
                ) : (
                    // Desktop: combined date + time picker inline
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            overflowX: "auto",
                        }}
                    >
                        <DatePicker
                            open
                            value={dt}
                            showTime={{ format: "HH:mm" }}
                            getPopupContainer={(triggerNode) =>
                                triggerNode.parentNode
                            }
                            onCalendarChange={(val) => {
                                if (val && !Array.isArray(val)) {
                                    setDt(val);
                                } else if (Array.isArray(val) && val[0]) {
                                    setDt(val[0]);
                                }
                            }}
                            onChange={(val) => {
                                setDt(val);
                            }}
                            style={hiddenInputStyle}
                            popupStyle={{ position: "static" }}
                            popupClassName={styles.noShadowPicker}
                            showNow={true}
                            needConfirm={false}
                            inputReadOnly
                        />
                    </div>
                )}
            </Modal>
        </>
    );
}
